import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Col, Card, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { CardContainer } from '../widgets/CardContainer';
import { DataTable, DataTableLoading, DataTableRecordsNotFound } from '../widgets/DataTable';
import { getScoutObservationList, getScoutObservationConfig } from '../../actions/scout-observation';
import { Heading5, Form } from '../widgets';
import { usePermission } from '../../helpers/common';
import { toDisplayDateString } from '../../helpers/moment-ex';
import { isAdmin } from '../../helpers/session';

const ScoutObservationList = () => {
    const dispatch = useDispatch();
    const { farmId } = useParams();
    const allow = usePermission();
    const isAllowFieldGroup = allow("field-grouping", "view");
    const isAllowScoutObservation = allow("scout-observation", "view");
    const [observationTypeId, setObservationTypeId] = useState(null)
    const isScoutObservationRequest = useSelector((state) => state.scoutObservation.isRequesting);
    const scoutObservationList = useSelector((state) => state.scoutObservation.scoutObservationList);
    // const isScoutObservationConfigRequest = useSelector((state) => state.scoutObservation.isRequesting);
    const scoutObservationConfig = useSelector((state) => state.scoutObservation.scoutObservationConfig);
    const [isComponentInitialLoad, setIsComponentInitialLoad] = useState(false);

    useEffect(() => {
        dispatch(getScoutObservationList({ farmId, is_current_user_observation: (isAdmin() || (isAllowFieldGroup && isAllowScoutObservation)) ? false : true }));
        dispatch(getScoutObservationConfig({ farmId }));
    }, [])

    useEffect(() => {
        if (scoutObservationConfig && scoutObservationConfig.observation_type && scoutObservationConfig.observation_type.length > 0) {
            setObservationTypeId(scoutObservationConfig.observation_type[0].observation_type_id);
            setIsComponentInitialLoad(true);
        }
    }, [scoutObservationConfig])

    const conditionalBasedFieldType = (observationFieldId, value) => {
        if (observationFieldId === 15) {
            return toDisplayDateString(value);
        } else if (observationFieldId === 5) {
            if (value && value === 'true') {
                return 'Yes';
            }
            return 'No';
        } else {
            return value;
        }
    }

    const customScoutObservationTableBody = (bodyKeyName, data) => {
        let finalHeaderLabel = "N/A";
        switch (bodyKeyName) {
            case 'file_upload':
                finalHeaderLabel = data.files;
                break;
            default:
                finalHeaderLabel = (data.observation_option_id && data.observation_option_id !== 'null') ? data.observation_option_value : conditionalBasedFieldType(data.observation_field_id, data.observation_value);
        }
        return finalHeaderLabel;
    }

    const removeUnwantedHeaderList = ["stand_count"];


    const { header, body, observationTypeList } = useMemo(() => {
        let payload = [
            {
                key: 'observation_type',
                label: 'Observation Type'
            },
            {
                key: 'scout_group_name',
                label: 'Scout Group Name'
            },
            {
                key: 'geo_region_shape_name',
                label: 'Field'
            }
        ]
        // for observationTypeList dropdown
        let observationTypeList = scoutObservationConfig && scoutObservationConfig.observation_type && scoutObservationConfig.observation_type.length > 0 ? scoutObservationConfig.observation_type.map((type) => {
            return (
                {
                    label: type.observation_type,
                    value: type.observation_type_id
                }
            )
        }) : []

        if (scoutObservationConfig && scoutObservationList.length > 0) {
            //Combing the field list by their names
            const configFields = {};
            scoutObservationConfig.fields && scoutObservationConfig.fields.length > 0 && scoutObservationConfig.fields.filter((sO) => sO.observation_type_id === observationTypeId).forEach((each) => {
                configFields[each.observation_field_id] = {
                    label: each.label,
                    key_name: each.key_name
                };
                if (each.hasOwnProperty('sub_fields') && each.sub_fields.length > 0) {
                    each.sub_fields.forEach((sub) => {
                        configFields[sub.observation_field_id] = {
                            label: sub.label,
                            key_name: sub.key_name
                        };

                    })
                }
            })

            //Appending observation field name and observation key name to the respected observations.
            let list = [...scoutObservationList.filter((sO) => sO.observation_type_id === observationTypeId)];
            if (list && list.length > 0) {
                list = list.map((each) => {
                    if (each && each.observations && each.observations.length > 0) {
                        each.observations = each.observations.map((obs) => {
                            if (configFields && configFields[obs.observation_field_id] && configFields[obs.observation_field_id].hasOwnProperty('label') && configFields[obs.observation_field_id].hasOwnProperty('key_name')) {
                                return { ...obs, observation_field_name: configFields[obs.observation_field_id]['label'], observation_field_key_name: configFields[obs.observation_field_id]['key_name'] }
                            }
                            return each;
                        })
                    }
                    return each;
                })
            }

            let header = [];
            let body = [];
            //payload for creating table header

            if (list && list.length > 0) {
                list.forEach((item) => {
                    //bodyContent for creating table body
                    let bodyContent = {
                        observation_type: item.observation_type,
                        scout_group_name: item.scout_group_name,
                        geo_region_shape_name: item.geo_region_shape_name,
                        created_by_name: item.created_by_name
                    }


                    if (item.observations && item.observations.length > 0) {
                        item.observations.forEach((each) => {
                            //appending observation list to payload for creating table header
                            if (!removeUnwantedHeaderList.includes(each.observation_field_key_name)) {
                                // Customize removing the header to show in table.
                                payload = [...payload, { key: each.observation_field_key_name, label: each.observation_field_name }];
                            }
                            bodyContent[each.observation_field_key_name] = customScoutObservationTableBody(each.observation_field_key_name, each);
                        })
                        body.push(bodyContent);
                    }
                    header = [...header, ...payload];
                    header.push({
                        key: "created_by_name",
                        label: "Created By"
                    });
                })
                //removing duplicate table header
                header = header.reduce((accumulator, current) => {
                    const existingObject = accumulator.find(obj => obj.key === current.key);
                    if (!existingObject) {
                        accumulator.push(current);
                    }
                    return accumulator;
                }, []);
            }
            if (body && body.length === 0 && isComponentInitialLoad) {
                if (observationTypeId !== observationTypeList.length) {
                    setObservationTypeId(observationTypeId + 1);
                } else {
                    setObservationTypeId(scoutObservationConfig.observation_type[0].observation_type_id);
                    setIsComponentInitialLoad(false);
                }
            }
            return { header: header.length > 0 ? header : payload, body, observationTypeList };
        }
        return { header: payload, body: [], observationTypeList: [] };

    }, [scoutObservationConfig, scoutObservationList, observationTypeId])


    const handleOnChange = (value) => {
        setObservationTypeId(value);
        setIsComponentInitialLoad(false);
    }

    const customScoutObservationTableHeader = (headerLabel) => {
        let finalHeaderLabel = "N/A";
        switch (headerLabel) {
            case 'Choose File':
                finalHeaderLabel = 'File';
                break;
            default:
                finalHeaderLabel = headerLabel;
        }
        return finalHeaderLabel;
    }

    const displayBodyContent = (body, head) => {
        if (body[head.key]) {
            if (head.key === 'file_upload') {
                if (body[head.key].length > 0) {
                    return body[head.key].map((fileUrl, index) => <a href={fileUrl} className='mr-2 text-nowrap' target="_blank" without rel="noopener noreferrer" >File {index + 1}</a>)
                } else {
                    return 'N/A';
                }
            } else {
                return body[head.key];
            }
        } else {
            return 'N/A'
        }
    }

    const handleRefresh = () => {
        dispatch(getScoutObservationList({ farmId, is_current_user_observation: (isAllowFieldGroup && isAllowScoutObservation) ? false : true }));
        dispatch(getScoutObservationConfig({ farmId }));
    }

    return (
        <CardContainer lg={12} md={12} className="hardware-list px-3 animate animate__fadeIn">
            <Card className="card-list">
                <Card.Header className="scout-observation-header">
                    <Row className="justify-content-between align-item-center mx-0">
                        <Card.Title>
                            <Heading5>Scout Observation List</Heading5>
                        </Card.Title>
                        <div className='d-flex align-items-center g-3'>
                            <i
                                title="Refresh"
                                className="fa fa-refresh btn-refresh cursor-pointer mb-2"
                                aria-hidden="true"
                                onClick={handleRefresh}
                            />
                            <Form.Group className='mb-0 pl-4'>
                                <div className='scout-observation-dropdown'>
                                    <Form.Label>Observation Type</Form.Label>
                                    <Form.Select
                                        className="observation-dropdown"
                                        placeholder="Select Scout Observation"
                                        name="observationTypeId"
                                        onChange={(e) => handleOnChange((e && e.value) ? Number(e.value) : null)}
                                        value={observationTypeId}
                                        options={observationTypeList}
                                        includeUnknown isClearable={false} />
                                </div>
                            </Form.Group>
                        </div>
                    </Row>
                </Card.Header>
                <Card.Body className="overflow-auto">
                    <Row className="m-0">
                        <Col className="p-0">
                            <DataTable className="tbl-fixed">
                                {header && header.length > 0 && (
                                    <React.Fragment>
                                        <DataTable.Header>
                                            <DataTable.Row>
                                                {header.map((head, index) => (
                                                    <DataTable.Cell key={index} style={{ width: "130px" }}>{customScoutObservationTableHeader(head.label)}</DataTable.Cell>
                                                ))}
                                            </DataTable.Row>
                                        </DataTable.Header>
                                        <DataTable.Body>
                                            {body && body.length > 0 && body.map((body, rowIndex) => (
                                                <DataTable.Row key={rowIndex} className={`item active}`}>
                                                    {header.map((head, colIndex) => (
                                                        <DataTable.Cell key={colIndex} style={{ width: "130px" }}>{displayBodyContent(body, head)}</DataTable.Cell>
                                                    ))}
                                                </DataTable.Row>
                                            ))}
                                        </DataTable.Body>
                                    </React.Fragment>
                                )}
                            </DataTable>
                            {isScoutObservationRequest && <DataTableLoading />}
                            {!isScoutObservationRequest && body.length === 0 && (
                                <DataTableRecordsNotFound />
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </CardContainer >
    )
}

export default ScoutObservationList;